"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Book, Users } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function HierophantCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-purple-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">The Hierophant</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The Hierophant</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-hierophant.svg"
                  alt="The Hierophant Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">V</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The Hierophant</h1>
                  <p className="text-xl text-purple-400">Ruhani Rehber / Öğretmen</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Ruhani öğretim, gelenek ve kurumsallığın kartı. Bilgiyi aktaran, rehberlik eden ve
                toplumsal değerleri koruyan kutsal figürün temsilidir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Öğretim</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Gelenek</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Rehberlik</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Ruhanilik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhani Rehberlik</h3>
                  <p className="text-slate-400 text-sm">Manevi öğretim, bilgelik aktarımı ve rehberlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Geleneksel Değerler</h3>
                  <p className="text-slate-400 text-sm">Kurumsal bilgi, aile gelenekleri ve toplumsal normlar.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Eğitim ve Öğretim</h3>
                  <p className="text-slate-400 text-sm">Bilgi paylaşımı, mentorluk ve öğrenme süreci.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Toplumsal Uyum</h3>
                  <p className="text-slate-400 text-sm">Grup aidiyeti, ortak değerler ve sosyal bağlılık.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Book className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dogmatizm</h3>
                  <p className="text-slate-400 text-sm">Katı kurallar, esnek olmayan yaklaşım.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gelenek Karşıtlığı</h3>
                  <p className="text-slate-400 text-sm">Kurallara başkaldırı, sıradışı yollar arama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kötü Tavsiye</h3>
                  <p className="text-slate-400 text-sm">Yanlış rehberlik, manipülatif öğretim.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Ruhani Kriz</h3>
                  <p className="text-slate-400 text-sm">İnanç kaybı, değer karmaşası, kimlik krizi.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🗝️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İki Anahtar</h3>
              <p className="text-slate-400 text-sm">Bilinçli ve bilinçsiz bilgi, ruhani sırlar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">✋</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kutsama Eli</h3>
              <p className="text-slate-400 text-sm">Ruhani güç aktarımı, kutsanmış öğretim.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-black text-lg">👑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Üçlü Taç</h3>
              <p className="text-slate-400 text-sm">Kutsal üçlü, ruhani hiyerarşi ve otorite.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İki Öğrenci</h3>
              <p className="text-slate-400 text-sm">Öğretim süreci, bilgi aktarımı ve öğrenme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏛️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Tapınak Sütunları</h3>
              <p className="text-slate-400 text-sm">Kurumsal yapı, geleneksel düzen ve istikrar.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌹</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Gül ve Haç</h3>
              <p className="text-slate-400 text-sm">Maddi ve manevi dengenin birleşimi.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Geleneksel evlilik yaklaşımı</li>
                <li>• Aile onayı ve toplumsal kabul</li>
                <li>• Ortak değerler paylaşımı</li>
                <li>• Kutsal bağ ve ruhani birlik</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Eğitim sektörü</li>
                <li>• Danışmanlık hizmetleri</li>
                <li>• Dini ve ruhani kurumlar</li>
                <li>• Geleneksel meslekler</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhani öğretim alma</li>
                <li>• Geleneksel bilgileri öğrenme</li>
                <li>• Mentor bulma</li>
                <li>• Toplumsal değerleri anlama</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Book className="h-5 w-5 text-blue-400" />
                Eğitim ve Öğrenme
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Formal eğitim süreçleri</li>
                <li>• Akademik başarı</li>
                <li>• Öğretmen-öğrenci ilişkisi</li>
                <li>• Kurumsal öğrenme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/emperor"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            The Emperor
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/lovers"
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            The Lovers
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}